import {
  combineReducers,
  configureStore as configureToolkitStore,
  createListenerMiddleware,
  ThunkAction
} from "@reduxjs/toolkit";
import {OtsAsyncThunkConfigExtra, otsMiddleware, otsReducers} from "@ots-frontend/ots-data";
import {Action} from "redux";
import {ThunkDispatch} from "redux-thunk";
import {logout} from "./auth/actions";
import {authReducer} from "./auth/auth-store";
import {createLogger} from "redux-logger";
import {mapReducer} from "./map/map-store";
import {uiReducer} from "./ui/ui-store";
import {
  CONTAINER_STATUS_PAGE_WIDTHS_KEY,
  containerStatusPageReducer
} from "./container-status-page/container-status-page-store";
import {ticketsPageReducer} from "./tickets-page/tickets-page-store";
import {insightsReducer} from "./insights/insights-store";
import {volumeInventorySearchPageReducer} from "./volume-inventory-search-page/volume-inventory-search-page-store";
import {
  allocatableLoadInventorySearchPageReducer
} from "./allocatable-load-inventory-search-page/allocatable-load-inventory-search-page-store";
import {
  containerDNUStatisticsSearchPageReducer
} from "./container-dnu-statistics-search-page/container-dnu-statistics-search-page-store";
import {pipelineInsightsReducer} from "./pipeline-insights/pipeline-insights-store";
import {otsUiReducers} from "@ots-frontend/ots-ui";
import {settlementPageReducer} from "./settlement-page/settlement-page-store";
import {pipelineSettlementPageReducer} from "./pipeline-settlement-page/pipeline-settlement-page-store";
import {
  exceedsNonPrimaryVolumeStatisticsSearchPageReducer
} from "./exceeds-non-primary-volume-statistics-search-page/exceeds-non-primary-volume-statistics-search-page-store";
import {setContainerStatusPageStateWidths} from "./container-status-page/actions";
import {
  CONTAINER_STATUS_SHUT_IN_PAGE_WIDTHS_KEY,
  containerStatusShutInPageReducer
} from "./container-status-shut-in-page/container-status-shut-in-page-store";
import {setContainerStatusShutInPageStateWidths} from "./container-status-shut-in-page/actions";

export type AppAsyncThunkConfigExtra = OtsAsyncThunkConfigExtra & {
  // extra props here
};

export type AppThunk<R = void> = ThunkAction<
  R,
  AppState,
  AppAsyncThunkConfigExtra,
  Action<string>
>;

export type AppThunkDispatch = ThunkDispatch<
  AppState,
  AppAsyncThunkConfigExtra,
  Action<string>
>;

export type AppAsyncThunkConfig = {
  state: AppState;
  dispatch: AppDispatch;
  extra: AppAsyncThunkConfigExtra;
  rejectValue: string;
};

const reducers = {
  ...otsReducers,
  ...otsUiReducers,
  auth: authReducer,
  map: mapReducer,
  ui: uiReducer,
  allocatableLoadInventorySearchPage: allocatableLoadInventorySearchPageReducer,
  containerDNUStatisticsSearchPage: containerDNUStatisticsSearchPageReducer,
  containerStatusPage: containerStatusPageReducer,
  containerStatusShutInPage: containerStatusShutInPageReducer,
  exceedsNonPrimaryVolumeStatisticsSearchPage: exceedsNonPrimaryVolumeStatisticsSearchPageReducer,
  ticketsPage: ticketsPageReducer,
  volumeInventorySearchPage: volumeInventorySearchPageReducer,
  insights: insightsReducer,
  pipelineInsights: pipelineInsightsReducer,
  pipelineSettlementPage: pipelineSettlementPageReducer,
  settlementPage: settlementPageReducer,
};

const appReducer = combineReducers(reducers);

export type AppState = ReturnType<typeof appReducer>;

// export default appReducer;

const rootReducer = (state: any, action: any) => {
  switch (action.type) {
    // clear out the application state when use logs out.
    case logout.type:
      state = undefined;
      break;
  }

  return appReducer(state, action);
};

export const storeExtra: OtsAsyncThunkConfigExtra = {
  clientApp: "hubgo-web",
  baseUrl: import.meta.env.VITE_BASE_API_URL,
  tenantCode: import.meta.env.VITE_TENANT_CODE,
  getAuthToken: (state: AppState): string | undefined => {
    return state.auth.authToken;
  },
  getRegionId: (state: AppState): string | undefined => {
    return state.entities.regions.currentRegionId;
  },
}

export const prefsMiddleware = createListenerMiddleware<AppState>()
prefsMiddleware.startListening({
  actionCreator: setContainerStatusPageStateWidths,
  effect: (action, api) => {
    localStorage.setItem(CONTAINER_STATUS_PAGE_WIDTHS_KEY, JSON.stringify(action.payload))
  }
})
prefsMiddleware.startListening({
  actionCreator: setContainerStatusShutInPageStateWidths,
  effect: (action, api) => {
    localStorage.setItem(CONTAINER_STATUS_SHUT_IN_PAGE_WIDTHS_KEY, JSON.stringify(action.payload))
  }
})

/**
 * Configure store for development environment.
 */
const configureDevStore = () => {
  return configureToolkitStore({
    reducer: rootReducer,
    devTools: true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: storeExtra,
          immutableCheck: false,
          serializableCheck: false
        }
      })
        .concat(createLogger())
        .concat(...otsMiddleware)
        .prepend(prefsMiddleware.middleware)
  });
};

/**
 * Configure store for production environment.
 */
const configureProdStore = () => {
  return configureToolkitStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: storeExtra
        }
      })
        .concat(...otsMiddleware)
        .prepend(prefsMiddleware.middleware)
  });
};

const configureStore = () => {
  if (import.meta.env.PROD) {
    return configureProdStore();
  } else {
    return configureDevStore();
  }
};

export const store = configureStore();
export type AppDispatch = typeof store.dispatch;
